import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for string validation
 */
export interface StringValidatorOptions extends BaseValidatorOptions {
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
}

/**
 * Validate a string value
 * @param value - The string value to validate
 * @param options - Validation options (minLength, maxLength, pattern)
 * @returns Validation result with the string value and optional error
 *
 * @example
 * ```typescript
 * const result = validateString("hello", { minLength: 3, maxLength: 10 });
 * if (result.error) {
 *   console.log(result.error.message);
 * }
 * ```
 */
export function validateString(value: string, options: StringValidatorOptions = {}): ValidationResult<string> {
  const { field = "value", minLength, maxLength, pattern } = options;

  if (minLength !== undefined && value.length < minLength) {
    return {
      value,
      error: {
        field,
        message: `"${field}" must be at least ${minLength} characters (got ${value.length})`,
        severity: "warning",
      },
    };
  }

  if (maxLength !== undefined && value.length > maxLength) {
    return {
      value,
      error: {
        field,
        message: `"${field}" must be at most ${maxLength} characters (got ${value.length})`,
        severity: "warning",
      },
    };
  }

  if (pattern && !pattern.test(value)) {
    return {
      value,
      error: {
        field,
        message: `"${field}" does not match required pattern: ${value}`,
        severity: "warning",
      },
    };
  }

  return { value };
}
